import clsx from 'clsx';
import type * as React from 'react';

import { Icon } from '@gitbook/spine-icons';

import { AtomButton } from '../Button';
import { Tooltip } from '../Tooltip';
import { type IconDefinition, renderIcon } from '../util';

export type ChipSize = 'small' | 'medium' | 'large';
export type ChipKind = 'primary' | 'secondary' | 'merge' | 'upgrade' | 'danger' | 'info';

export type ChipProps = React.HTMLAttributes<HTMLDivElement> & {
    kind?: ChipKind;
    size?: ChipSize;
    icon?: IconDefinition;
    children: React.ReactNode;
    /**
     * Callback fired when the delete button is pressed.
     * If defined, the delete button is displayed.
     * */
    onDelete?: () => void;
};

/**
 * Chips represent a set of interactive keywords. Chips can be added or removed.
 */
export function Chip(props: ChipProps) {
    const {
        children,
        icon: IconComponent,
        size = 'medium',
        kind = 'secondary',
        className,
        onDelete,
        ...rest
    } = props;

    const { height, padding, iconSize, text, deleteMargin } = sizeStyles[size];

    return (
        <div
            className={clsx(
                'inline-flex w-fit items-center justify-start overflow-hidden rounded-sm border',
                height,
                padding,
                kindStyles[kind],
                className
            )}
            {...rest}
        >
            {IconComponent
                ? renderIcon(IconComponent, { size: iconSize, className: 'shrink-0' })
                : null}
            {children ? (
                <span className={clsx(text, IconComponent ? 'ml-2' : undefined)}>{children}</span>
            ) : null}

            {onDelete ? (
                <Tooltip label="Delete">
                    <AtomButton
                        onPress={onDelete}
                        className={clsx(deleteMargin, 'group/delete flex shrink-0 items-center')}
                    >
                        {renderIcon(Icon.X, {
                            size: iconSize,
                            className: clsx('shrink-0', iconKindStyles[kind]),
                        })}
                    </AtomButton>
                </Tooltip>
            ) : null}
        </div>
    );
}

const kindStyles: Record<ChipKind, string> = {
    primary: 'bg-primary-ui text-primary-low border-transparent',
    secondary: 'bg-(--neutral-3) text-neutral-low border-transparent',
    merge: 'bg-merge-ui text-merge-low border-transparent',
    upgrade: 'bg-upgrade-ui text-upgrade-low border-transparent',
    danger: 'bg-danger-ui text-danger-low border-transparent',
    info: 'bg-info-ui text-info-low border-transparent',
};
const iconKindStyles: Record<ChipKind, string> = {
    primary: 'text-primary-low group-hover/delete:text-primary-high',
    secondary: 'text-neutral-low group-hover/delete:text-neutral-high',
    merge: 'text-merge-low group-hover/delete:text-merge-high',
    upgrade: 'text-upgrade-low group-hover/delete:text-upgrade-high',
    danger: 'text-danger-low group-hover/delete:text-danger-high',
    info: 'text-info-low group-hover/delete:text-info-high',
};

const sizeStyles: Record<
    ChipSize,
    { height: string; padding: string; text: string; iconSize: number; deleteMargin: string }
> = {
    small: {
        height: 'h-6',
        padding: 'px-1',
        text: 'text-ui-small',
        iconSize: 12,
        deleteMargin: 'ml-1',
    },
    medium: {
        height: 'h-7',
        padding: 'px-2',
        text: 'text-ui-action',
        iconSize: 16,
        deleteMargin: 'ml-2',
    },
    large: {
        height: 'h-8',
        padding: 'px-3',
        text: 'text-content-paragraph',
        iconSize: 18,
        deleteMargin: 'ml-3',
    },
};
